/**
 * The New contest dialog for a Pick'em pool (ISSUE-1429). Mounted once by
 * `PoolScreenPane` for the Commissioner and Admins of a Pick'em pool and
 * opened by the same `?dialog=new-contest` as Survivor's, from the Game tab's
 * ended state and the Contests card in Settings.
 *
 * It copies the last contest's settings (or the league's starting values when
 * there was none), leaves the dates blank so the server starts the contest the
 * day after the last one ended, and saves through /pickem/contest-create. The
 * server decides whether a start is allowed; each refusal shows inline and the
 * dialog stays open. Edits are guarded on close.
 */
import {
  usePickemContestCreate,
  usePickemPoolGet,
  usePickemSetupPreview,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
  Input,
  OVERLAY_FOOTER_LAYOUT,
  Skeleton,
} from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import {
  CREATE_POOL_MISSING_ELEMENT_ID,
  CreatePoolMissingSummary,
} from "../createPool/CreatePoolMissingSummary";
import {
  contestNameError,
  NEW_CONTEST_CANCEL_LABEL,
  NEW_CONTEST_CLOSE_LABEL,
  NEW_CONTEST_DIALOG_DESCRIPTION,
  NEW_CONTEST_DIALOG_NAME,
  NEW_CONTEST_DIALOG_TITLE,
  NEW_CONTEST_DISCARD_BODY,
  NEW_CONTEST_DISCARD_CANCEL_LABEL,
  NEW_CONTEST_DISCARD_CONFIRM_LABEL,
  NEW_CONTEST_DISCARD_TITLE,
  NEW_CONTEST_FAILED_MESSAGE,
  NEW_CONTEST_LOADING_LABEL,
  NEW_CONTEST_NAME_LABEL,
  NEW_CONTEST_NAME_MAX_LENGTH,
  NEW_CONTEST_RETRY_LABEL,
  NEW_CONTEST_SUBMIT_LABEL,
  NEW_CONTEST_SUBMITTING_LABEL,
  newContestPrefillNote,
  validateContestName,
} from "../newContest/newContestModel";
import { buildPickemSetupPayload } from "./pickemCreateSetupDraft";
import {
  buildPickemContestCreateBody,
  buildPickemNewContestDraft,
  isPickemNewContestDirty,
  PICKEM_NEW_CONTEST_START_HINT,
  pickemNewContestFailure,
  pickemNewContestMissingItems,
  type typePickemNewContestDraft,
} from "./pickemNewContestModel";
import { PickemRulesPreview } from "./PickemRulesLines";
import { PickemSettingsFields } from "./PickemSettingsFields";

const DEFAULTS_STALE_TIME_MS = 5 * 60 * 1000;

// The reads a new contest changes, refreshed by hand so the Game tab and the
// Contests card show it the moment the dialog closes.
const REFRESHED_QUERY_PREFIXES = [
  "post:contest:contestList",
  "post:contest:contestGet",
  "post:pool:poolGet",
];

export type typePickemNewContestDialogProps = {
  poolId: string;
  leagueKey: string;
};

export const PickemNewContestDialog = (
  props: typePickemNewContestDialogProps,
): ReactNode => {
  const { poolId, leagueKey } = props;
  const overlay = useOverlay();
  const queryClient = useQueryClient();
  const open = overlay.isOpen("dialog", NEW_CONTEST_DIALOG_NAME);

  const pool = usePickemPoolGet({ poolId }, { enabled: open });
  const defaults = usePickemSetupPreview(
    { leagueKey },
    {
      enabled: open && leagueKey !== "",
      staleTime: DEFAULTS_STALE_TIME_MS,
    },
  );
  const create = usePickemContestCreate();

  const [draft, setDraft] = useState<typePickemNewContestDraft | null>(null);
  const [baseline, setBaseline] = useState<typePickemNewContestDraft | null>(
    null,
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [inlineNote, setInlineNote] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const hasFocusedRef = useRef(false);

  const poolData = pool.data?.data ?? null;
  const defaultsData = defaults.data?.data ?? null;
  const lastContest = poolData?.contest ?? null;
  const hasFailed =
    pool.isError ||
    defaults.isError ||
    extractErrorCode(pool.data) !== null ||
    extractErrorCode(defaults.data) !== null;

  useEffect(() => {
    if (!open || draft !== null || poolData === null || defaultsData === null) {
      return;
    }
    const initial = buildPickemNewContestDraft({
      lastSettings: poolData.settings,
      leagueDefaults: defaultsData.defaults,
      leagueKey,
    });
    setDraft(initial);
    setBaseline(initial);
  }, [open, draft, poolData, defaultsData, leagueKey]);

  useEffect(() => {
    if (open) return;
    setDraft(null);
    setBaseline(null);
    setNameError(null);
    setInlineNote(null);
    hasFocusedRef.current = false;
  }, [open]);

  // Dialog's own autofocus runs while the body is still loading, so the name
  // field is focused here the first time it appears.
  const hasDraft = draft !== null;
  useEffect(() => {
    if (!hasDraft || hasFocusedRef.current) return;
    hasFocusedRef.current = true;
    nameInputRef.current?.focus();
  }, [hasDraft]);

  const payload = draft === null ? null : buildPickemSetupPayload(draft.setup);
  const preview = usePickemSetupPreview(
    { leagueKey, setup: payload },
    {
      enabled: open && leagueKey !== "" && payload !== null,
      placeholderData: (previous) => previous,
    },
  );
  const previewData = preview.data?.data ?? null;

  const isDirty =
    draft !== null &&
    baseline !== null &&
    isPickemNewContestDirty(draft, baseline);

  function close(): void {
    overlay.close({ kind: "dialog" });
  }

  async function requestClose(): Promise<void> {
    if (!isDirty) {
      close();
      return;
    }
    const discard = await confirmDestructive({
      title: NEW_CONTEST_DISCARD_TITLE,
      body: NEW_CONTEST_DISCARD_BODY,
      confirmLabel: NEW_CONTEST_DISCARD_CONFIRM_LABEL,
      cancelLabel: NEW_CONTEST_DISCARD_CANCEL_LABEL,
    });
    if (discard) close();
  }

  async function submit(): Promise<void> {
    if (draft === null) return;
    const validity = validateContestName(draft.name);
    if (!validity.isValid) {
      setNameError(contestNameError(validity));
      return;
    }
    const body = buildPickemContestCreateBody({ poolId, draft });
    if (body === null) return;

    setInlineNote(null);
    try {
      const result = await create.mutateAsync(body);
      const code = extractErrorCode(result);
      if (code !== null) {
        const rawReason = result.error?.details?.reason;
        const failure = pickemNewContestFailure({
          code,
          reason: typeof rawReason === "string" ? rawReason : null,
        });
        if (failure.kind === "name") {
          setNameError(failure.message);
          return;
        }
        if (failure.kind === "inline") {
          setInlineNote(failure.note);
          return;
        }
        showApiError(result);
        return;
      }
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const first = query.queryKey[0];
          return (
            typeof first === "string" &&
            (first.startsWith("post:pickem:") ||
              REFRESHED_QUERY_PREFIXES.includes(first))
          );
        },
      });
      close();
    } catch (error) {
      if (!showApiError(error)) showOfflineError();
    }
  }

  let body: ReactNode;
  let footer: ReactNode;
  if (hasFailed) {
    body = (
      <div data-testid="pickem-new-contest-failed">
        <InlineBanner
          variant="warning"
          message={NEW_CONTEST_FAILED_MESSAGE}
          action={{
            label: NEW_CONTEST_RETRY_LABEL,
            onAction: () => {
              void pool.refetch();
              void defaults.refetch();
            },
          }}
        />
      </div>
    );
  } else if (draft === null) {
    body = (
      <div data-testid="pickem-new-contest-loading">
        <Skeleton shape="card" label={NEW_CONTEST_LOADING_LABEL} />
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-6" data-testid="pickem-new-contest">
        <Input
          ref={nameInputRef}
          label={NEW_CONTEST_NAME_LABEL}
          required
          maxLength={NEW_CONTEST_NAME_MAX_LENGTH}
          value={draft.name}
          error={nameError ?? undefined}
          onChange={(event) => {
            setDraft({ ...draft, name: event.target.value });
            setNameError(null);
          }}
          onBlur={() => {
            setNameError(contestNameError(validateContestName(draft.name)));
          }}
          data-testid="pickem-new-contest-name"
        />
        <div className="flex flex-col gap-1">
          {lastContest !== null ? (
            <p
              className="text-caption text-ink-secondary"
              data-testid="pickem-new-contest-prefill-note"
            >
              {newContestPrefillNote(lastContest.name)}
            </p>
          ) : null}
          <p className="text-caption text-ink-secondary">
            {PICKEM_NEW_CONTEST_START_HINT}
          </p>
        </div>
        <PickemSettingsFields
          draft={draft.setup}
          onChange={(setup) => {
            setDraft({ ...draft, setup });
          }}
          hasSpreads={
            previewData?.hasSpreads ?? defaultsData?.hasSpreads ?? true
          }
          fieldErrors={payload === null ? [] : (previewData?.fieldErrors ?? [])}
        />
        <PickemRulesPreview
          isDraftComplete={payload !== null}
          rulesLines={previewData?.rulesLines ?? null}
          testId="pickem-new-contest-rules"
        />
      </div>
    );
  }

  if (draft !== null && !hasFailed) {
    const missing = pickemNewContestMissingItems(draft);
    footer = (
      <div className="flex w-full flex-col gap-2">
        {/* ISSUE-1429. A refusal shows right above Create contest, in the
            footer that never scrolls, so it is on screen where the tap was. */}
        {inlineNote !== null ? (
          <div data-testid="pickem-new-contest-inline-note">
            <InlineBanner variant="warning" message={inlineNote} />
          </div>
        ) : null}
        <CreatePoolMissingSummary items={missing} />
        <div className={OVERLAY_FOOTER_LAYOUT}>
          <Button
            variant="secondary"
            onClick={() => {
              void requestClose();
            }}
            data-testid="pickem-new-contest-cancel"
          >
            {NEW_CONTEST_CANCEL_LABEL}
          </Button>
          <Button
            loading={create.isPending}
            loadingLabel={NEW_CONTEST_SUBMITTING_LABEL}
            disabled={missing.length > 0 || create.isPending}
            aria-describedby={
              missing.length > 0 ? CREATE_POOL_MISSING_ELEMENT_ID : undefined
            }
            onClick={() => {
              void submit();
            }}
            data-testid="pickem-new-contest-submit"
          >
            {NEW_CONTEST_SUBMIT_LABEL}
          </Button>
        </div>
      </div>
    );
  } else {
    footer = (
      <Button
        variant="secondary"
        onClick={close}
        data-testid="pickem-new-contest-close"
      >
        {NEW_CONTEST_CLOSE_LABEL}
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      onRequestClose={() => {
        void requestClose();
      }}
      isDirty={isDirty}
      title={NEW_CONTEST_DIALOG_TITLE}
      description={NEW_CONTEST_DIALOG_DESCRIPTION}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
